import { useIsTabletUp } from "@/shared/hooks/useMediaQuery";
import { useLocationPickerStore } from "../locationPickerStore";
import { LocationPickerOverlay } from "./LocationPickerOverlay";

/**
 * Point de montage du sélecteur d'emplacement. Ne rend rien tant que personne
 * n'appelle `pick()`.
 *
 * ## Deux emprises selon le format
 * - **< md** : `fixed inset-0`. Sur un téléphone, choisir un point sur une carte
 *   mérite tout l'écran, et il n'y a de toute façon aucun cadre à préserver.
 * - **≥ md** : `absolute inset-0`, borné à la zone de contenu par le conteneur
 *   qui l'accueille dans `AppShell`. La sidebar et la colonne contextuelle
 *   restent visibles — c'est l'emprise qu'avait l'ancien écran de route, avant
 *   qu'il ne devienne une surcouche. Une carte qui recouvre la navigation d'un
 *   grand écran ne gagne rien et fait perdre le repère.
 *
 * `key` sur la demande : deux sélections successives repartent d'un état neuf
 * (recherche vide, suggestions closes) au lieu de rouvrir sur les restes de la
 * précédente.
 */
export function LocationPickerHost() {
  const request = useLocationPickerStore((s) => s.request);
  const close = useLocationPickerStore((s) => s.close);
  const isTabletUp = useIsTabletUp();

  if (!request) return null;

  const picker = (
    <LocationPickerOverlay
      key={`${request.initial?.latitude ?? "na"},${request.initial?.longitude ?? "na"},${request.title ?? ""}`}
      initial={request.initial}
      title={request.title}
      onDone={close}
    />
  );

  if (request.presentation === "modal") {
    return (
      <div
        className="fixed inset-0 z-[2000] flex items-center justify-center bg-navy-900/65 p-3 backdrop-blur-[2px] sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-label={request.title ?? "Choisir l’emplacement"}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) close(null);
        }}
      >
        <div className="h-[min(820px,calc(100dvh-1.5rem))] w-full max-w-5xl overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl sm:h-[min(820px,calc(100dvh-3rem))]">
          {picker}
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        isTabletUp ? "absolute inset-0 z-40 bg-white" : "fixed inset-0 z-[2000] bg-white"
      }
      role="dialog"
      aria-modal="true"
    >
      {picker}
    </div>
  );
}
